// src/features/hotels/components/zones/HotelZones.tsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useDataProvider,
  useNotify,
  useTranslate,
  SimpleForm,
  useRecordContext,
  LoadingIndicator,
} from 'react-admin';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    IconButton,
    Grid,
    Tooltip,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Add as AddIcon,
    Business as ZoneIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    LocationOn as LocationIcon,
} from '@mui/icons-material';

// 🎯 SYSTEM COMPONENTS
import { MyToolbar } from '../../../../components/forms/MyToolbar';
import { MyFormContainer } from '../../../../components/forms/MyFormContainer';
import { 
  MyFormLayout, 
  FormCard, 
  MyFormSection 
} from '../../../../components/forms/MyFormLayout';

import { useHotelPermissions } from '../../hooks/useHotelPermissions';
import { Zone, Hotel } from '../../types';

interface ZoneFormData {
    name: string;
    description?: string;
    isActive: boolean;
    parentZoneId?: string;
}

/**
 * DYNAMIC TITLE
 */
const HotelZonesTitle: React.FC = () => {
  const record = useRecordContext<Hotel>();
  const translate = useTranslate();
  
  if (!record) return <LoadingIndicator />;
  
  return (
    <span>
      {translate('resources.hotels.zones.title')} - {record.name}
    </span>
  );
};

/**
 * ZONE CARD COMPONENT
 * Displays individual zone information in a card format
 */
const ZoneCard: React.FC<{
    zone: Zone;
    onEdit: (zone: Zone) => void;
    onDelete: (zone: Zone) => void;
    canEdit: boolean;
    canDelete: boolean;
    translate: any;
}> = ({ zone, onEdit, onDelete, canEdit, canDelete, translate }) => {
    return (
        <Card 
            variant="outlined" 
            sx={{ 
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                transition: 'all 0.3s ease',
                '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }
            }}
        >
            <CardContent>
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} sm={5}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <LocationIcon color="action" fontSize="small" />
                            <Typography variant="subtitle1" fontWeight="medium">
                                {zone.name}
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={5}>
                        {zone.description ? (
                            <Typography variant="body2" color="text.secondary">
                                {zone.description}
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.disabled" fontStyle="italic">
                                {translate('resources.hotels.zones.no_description')}
                            </Typography>
                        )}
                    </Grid>
                    
                    <Grid item xs={12} sm={2}>
                        <Box display="flex" justifyContent="flex-end" gap={1}>
                            {canEdit && (
                                <Tooltip title={translate('resources.hotels.zones.edit')}>
                                    <IconButton
                                        size="small"
                                        onClick={() => onEdit(zone)}
                                        color="primary"
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: 'primary.lighter',
                                            }
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {canDelete && (
                                <Tooltip title={translate('resources.hotels.zones.delete')}>
                                    <IconButton
                                        size="small"
                                        onClick={() => onDelete(zone)}
                                        color="error"
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: 'error.lighter',
                                            }
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Grid>
                </Grid>
                
                {/* Additional zone metadata if available */}
                {zone.lockerCount !== undefined && zone.lockerCount > 0 && (
                    <Box mt={2} pt={2} borderTop={1} borderColor="divider">
                        <Typography variant="caption" color="text.secondary">
                            {translate('resources.hotels.zones.lockers')}: {zone.lockerCount}
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

const HotelZones: React.FC = () => {
    // React Admin hooks and navigation
    const { id: hotelId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const record = useRecordContext<Hotel>();

    // Local state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hotel, setHotel] = useState<Hotel | null>(record || null);
    const [zones, setZones] = useState<Zone[]>([]);

    // Form state
    const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
    const [editingZone, setEditingZone] = useState<Zone | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [zoneToDelete, setZoneToDelete] = useState<Zone | null>(null);

    // Zone form
    const [zoneForm, setZoneForm] = useState<ZoneFormData>({
        name: '',
        description: '',
        isActive: true,
        parentZoneId: undefined,
    });

    // Permissions - pass complete hotel object
    const permissions = useHotelPermissions(hotel);

    // Load initial data
    useEffect(() => {
        if (hotelId) {
            loadData();
        }
    }, [hotelId]);

    // Update hotel from context
    useEffect(() => {
        if (record) {
            setHotel(record);
        }
    }, [record]);

    /**
     * Validate that hotelId exists
     */
    useEffect(() => {
        if (!hotelId) {
            notify(translate('resources.hotels.zones.invalid_hotel_id'), { type: 'error' });
            navigate('/hotels');
        }
    }, [hotelId, notify, navigate, translate]);

    /**
     * Load zones for the hotel
     */
    const loadData = async () => {
        if (!hotelId) return;

        try {
            setLoading(true);
            
            // If we don't have hotel info from context, load it
            if (!hotel) {
                try {
                    const hotelResponse = await dataProvider.getOne('hotels', { id: hotelId });
                    setHotel(hotelResponse.data);
                } catch (hotelError) {
                    console.error('Error loading hotel data:', hotelError);
                    notify(translate('resources.hotels.zones.hotel_load_error'), { type: 'error' });
                    return;
                }
            }
            
            // Load hotel zones
            const zonesResponse = await (dataProvider as any).getHotelZones(hotelId);
            
            // Verify valid response
            if (zonesResponse && Array.isArray(zonesResponse.data)) {
                setZones(zonesResponse.data);
            } else {
                // If no valid data, set empty array (not an error)
                setZones([]);
            }
        } catch (error) {
            console.error('Error loading hotel zones data:', error);
            
            // Check if it's a real error or just no data
            const errorMessage = (error as Error)?.message || '';
            
            // If it's a "not found" error, it's not really an error
            if (errorMessage.includes('404') || errorMessage.includes('not found')) {
                console.info('No zones found for hotel, showing empty state');
                setZones([]);
            } else {
                // Only show error if it's a real connectivity/server error
                notify(translate('resources.hotels.zones.load_error'), { type: 'error' });
                setZones([]); // Still allow zone creation
            }
        } finally {
            setLoading(false);
        }
    };

    /**
     * Open dialog to create a new zone
     */
    const handleAddZone = () => {
        if (!permissions.canEdit) {
            notify(translate('resources.hotels.zones.no_permissions_create'), { type: 'warning' });
            return;
        }

        setZoneForm({
            name: '',
            description: '',
            isActive: true,
            parentZoneId: undefined,
        });
        setEditingZone(null);
        setZoneDialogOpen(true);
    };

    /**
     * Open dialog to edit an existing zone
     */
    const handleEditZone = (zone: Zone) => {
        if (!permissions.canEdit) {
            notify(translate('resources.hotels.zones.no_permissions_edit'), { type: 'warning' });
            return;
        }

        setZoneForm({
            name: zone.name,
            description: zone.description || '',
            isActive: zone.isActive !== undefined ? zone.isActive : true,
            parentZoneId: zone.parentZoneId,
        });
        setEditingZone(zone);
        setZoneDialogOpen(true);
    };

    /**
     * Save a zone (create or update)
     */
    const handleSaveZone = async () => {
        if (!hotelId || !permissions.canEdit) {
            if (!permissions.canEdit) {
                notify(translate('resources.hotels.zones.no_permissions_save'), { type: 'warning' });
            }
            return;
        }

        // Basic validations
        if (!zoneForm.name.trim()) {
            notify(translate('resources.hotels.zones.name_required'), { type: 'warning' });
            return;
        }

        // Check unique name
        const existingZone = zones.find(z => 
            z.name.toLowerCase() === zoneForm.name.toLowerCase() && z.id !== editingZone?.id
        );
        if (existingZone) {
            notify(translate('resources.hotels.zones.name_exists'), { type: 'warning' });
            return;
        }

        try {
            setSaving(true);

            // Prepare data without order field
            const zoneData = {
                name: zoneForm.name,
                description: zoneForm.description || null,
                isActive: true, // Always true as per backend
                parentZoneId: zoneForm.parentZoneId || null
            };

            let response;
            if (editingZone) {
                // Update existing zone
                response = await (dataProvider as any).updateHotelZone(editingZone.id, zoneData, hotelId);
                
                setZones(prev => 
                    prev.map(zone => 
                        zone.id === editingZone.id ? response.data : zone
                    )
                );
                notify(translate('resources.hotels.zones.updated_success'), { type: 'success' });
            } else {
                // Create new zone
                response = await (dataProvider as any).createHotelZone(hotelId, zoneData);
                
                setZones(prev => [...prev, response.data]);
                notify(translate('resources.hotels.zones.created_success'), { type: 'success' });
            }

            setZoneDialogOpen(false);
            setEditingZone(null);
        } catch (error) {
            console.error('Error saving zone:', error);
            notify(editingZone ? translate('resources.hotels.zones.update_error') : translate('resources.hotels.zones.create_error'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    /**
     * Initiate zone deletion
     */
    const handleDeleteClick = (zone: Zone) => {
        if (!permissions.canDelete) {
            notify(translate('resources.hotels.zones.no_permissions_delete'), { type: 'warning' });
            return;
        }

        setZoneToDelete(zone);
        setDeleteDialogOpen(true);
    };

    /**
     * Confirm zone deletion
     */
    const handleDeleteConfirm = async () => {
        if (!zoneToDelete || !permissions.canDelete) {
            if (!permissions.canDelete) {
                notify(translate('resources.hotels.zones.no_permissions_delete'), { type: 'warning' });
            }
            return;
        }

        try {
            setSaving(true);
            
            // Use specific dataProvider function
            await (dataProvider as any).deleteHotelZone(zoneToDelete.id, hotelId);
            setZones(prev => prev.filter(zone => zone.id !== zoneToDelete.id));
            notify(translate('resources.hotels.zones.deleted_success'), { type: 'success' });
            setDeleteDialogOpen(false);
            setZoneToDelete(null);
        } catch (error) {
            console.error('Error deleting zone:', error);
            notify(translate('resources.hotels.zones.delete_error'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <MyFormContainer fullWidth={true}>
            <SimpleForm
                toolbar={<MyToolbar showDelete={false} resource="hotels" />}
            >
                <MyFormLayout>
                    <FormCard xs={12}>
                        <MyFormSection
                            title={translate('resources.hotels.zones.title')}
                            subtitle={translate('resources.hotels.zones.subtitle')}
                            icon={<ZoneIcon />}
                            variant='outlined'
                    
                        >
                         
                               
                        {/* Header section with count and add button */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                {translate('resources.hotels.zones.count', { 
                                    smart_count: zones.length,
                                    count: zones.length 
                                })}
                            </Typography>
                            
                            {permissions.canEdit && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddZone}
                                >
                                    {translate('resources.hotels.zones.new_zone')}
                                </Button>
                            )}
                        </Box>

                        {/* Zones list */}
                        {zones.length === 0 ? (
                            <Box 
                                sx={{ 
                                    textAlign: 'center', 
                                    py: 6,
                                    px: 2,
                                    backgroundColor: 'grey.50',
                                    borderRadius: 2,
                                    border: '1px dashed',
                                    borderColor: 'grey.300',
                                    minHeight: 200,
                                }}
                            >
                                <LocationIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                                <Typography variant="h6" gutterBottom color="text.secondary">
                                    {translate('resources.hotels.zones.no_zones')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    {translate('resources.hotels.zones.no_zones_description')}
                                </Typography>
                                {permissions.canEdit && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddZone}
                                        sx={{ mt: 2 }}
                                    >
                                        {translate('resources.hotels.zones.create_first_zone')}
                                    </Button>
                                )}
                            </Box>
                        ) : (
                            <Box
                            sx={{minHeight: 200,}}>
                                {zones.map((zone) => (
                                    <ZoneCard
                                        key={zone.id}
                                        zone={zone}
                                        onEdit={handleEditZone}
                                        onDelete={handleDeleteClick}
                                        canEdit={permissions.canEdit}
                                        canDelete={permissions.canDelete}
                                        translate={translate}
                                    />
                                ))}
                            </Box>
                        )}
                             
                        
                        </MyFormSection>
                    </FormCard>
                </MyFormLayout>
            </SimpleForm>

            {/* Zone dialog */}
            <Dialog 
                open={zoneDialogOpen} 
                onClose={() => !saving && setZoneDialogOpen(false)}
                maxWidth="sm" 
                fullWidth
            >
                <DialogTitle>
                    {editingZone ? translate('resources.hotels.zones.edit_zone') : translate('resources.hotels.zones.new_zone')}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            autoFocus
                            label={translate('resources.hotels.zones.zone_name')}
                            value={zoneForm.name}
                            onChange={(e) => setZoneForm(prev => ({ ...prev, name: e.target.value }))}
                            fullWidth
                            required
                            helperText={translate('resources.hotels.zones.name_helper')}
                        />

                        <TextField
                            label={translate('resources.hotels.zones.description')}
                            value={zoneForm.description}
                            onChange={(e) => setZoneForm(prev => ({ ...prev, description: e.target.value }))}
                            fullWidth
                            multiline
                            rows={3}
                            helperText={translate('resources.hotels.zones.description_helper')}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setZoneDialogOpen(false)} disabled={saving}>
                        {translate('resources.hotels.zones.cancel')}
                    </Button>
                    <Button 
                        onClick={handleSaveZone}
                        variant="contained"
                        disabled={saving || !zoneForm.name.trim()}
                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                        {saving ? translate('resources.hotels.zones.saving') : (editingZone ? translate('resources.hotels.zones.update') : translate('resources.hotels.zones.create'))}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog 
                open={deleteDialogOpen} 
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>{translate('resources.hotels.zones.delete_zone')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {translate('resources.hotels.zones.delete_confirm', { name: zoneToDelete?.name })}
                    </Typography>
                    {zoneToDelete?.lockerCount && zoneToDelete.lockerCount > 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {translate('resources.hotels.zones.delete_warning', { count: zoneToDelete.lockerCount })}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        {translate('resources.hotels.zones.cancel')}
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        color="error" 
                        variant="contained"
                        disabled={saving}
                    >
                        {saving ? translate('resources.hotels.zones.deleting') : translate('resources.hotels.zones.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </MyFormContainer>
    );
};

export default HotelZones;